import type { MultipleOpportunity, SingleOpportunity } from '@ps/web-sports-api-types';
import React from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import type { OpportunityInput } from '@swf/core/models/sports/betSlip/types';
import { getIsAvailableOpportunity } from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';

import { useGetSortedApplicableWallets } from '../../../../../../hooks/useGetSortedApplicableWallets';

import styles from './PromotionPicker.scss';
import Promotion from './components/Promotion/Promotion';

interface Props {
  opportunity: SingleOpportunity | MultipleOpportunity;
}

const PromotionPicker: React.FC<Props> = ({ opportunity }) => {
  const { input } = useField<OpportunityInput | undefined>(opportunity.id, {
    subscription: { value: true }
  });
  const isPromotionPickerOpen = useSelector((rootState: RootState) =>
    select.betSlip.isPromotionPickerOpen(rootState, opportunity.id)
  );
  const allEnabledWallets = useSelector(select.extraWallets.getAllEnabledWallets);
  const applicableWallets = getIsAvailableOpportunity(opportunity)
    ? opportunity.applicableWallets || []
    : [];
  const sortedApplicableWallets = useGetSortedApplicableWallets(
    applicableWallets,
    allEnabledWallets
  );

  if (sortedApplicableWallets.length < 2) {
    return null;
  }

  if (!isPromotionPickerOpen || !getIsAvailableOpportunity(opportunity)) {
    return null;
  }

  const actualNumberOfLines = input.value?.eachWaySelected
    ? opportunity.numberOfLines * 2
    : opportunity.numberOfLines;

  return (
    <div id={opportunity.id} className={styles.container}>
      {sortedApplicableWallets.map((wallet) => (
        <Promotion
          key={wallet.id}
          wallet={wallet}
          opportunity={opportunity}
          numberOfLines={actualNumberOfLines}
        />
      ))}
    </div>
  );
};

export default PromotionPicker;
